import { stringify } from 'query-string';
import React, { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';

import AuthLayout from '@dashboard/layouts/AuthLayout';

import storage from '@dashboard/services/storage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { tracking } from '@dashboard/lib/tracking';
import { getGclid } from '@dashboard/lib/gclid';

import Form from '@dashboard/components/Formik/Form';
import Meta from '@dashboard/components/Meta';
import { GoogleSignIn } from '@dashboard/components/GoogleSignIn';
import {
  AuthDivider,
  AuthErrorLink,
  AuthFieldError,
  AuthFooter,
  AuthHeader,
  AuthTerms,
  AuthLink,
  authButtonClassName,
  authInputClassName,
  authRedirectPath,
  invalidFieldPropsAreEqual,
  newAccountPath,
  newPasswordField,
  newPasswordSchema,
  renderAuthField,
} from '@dashboard/components/AuthForm';

import { signup, prefetchBrandFromEmail } from './helpers';
import { appUrl } from '@/dashboard/services/env';

// The signup API answers a taken email with 303, not a 4xx.
const isEmailTaken = response => response?.meta?.status === 303;

export default function Signup() {
  const { setLogged } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const prefilledEmail = location.state?.email || '';
  const [email, setEmail] = useState(prefilledEmail);
  const { template: createFromTemplateId, page: createFromPageId } = useUrlQuery();
  const prefetchedDomainsRef = useRef(new Set());
  const [takenEmail, setTakenEmail] = useState(null);

  const handleEmailBlur = useCallback(e => {
    const email = e.target.value;
    if (!email || !email.includes('@')) return;

    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain || prefetchedDomainsRef.current.has(domain)) return;

    prefetchedDomainsRef.current.add(domain);

    prefetchBrandFromEmail(domain).catch(() => {});
  }, []);

  return (
    <>
      <Meta
        meta={{
          title: 'Signup',
        }}
      >
        <link rel="canonical" href={`${appUrl}/signup`} />
      </Meta>

      <AuthLayout legalLinks={false}>
        <AuthHeader
          title="Create your free account"
          description="Sell products, subscriptions, event tickets and services on your own Stripe account."
        />

        <GoogleSignIn mode="signup" marketingConsent={true} />

        <AuthDivider />

        <Form
          initialValues={{
            email: prefilledEmail,
            password: '',
            marketingConsent: true,
          }}
          validationSchema={newPasswordSchema}
          validateOnBlur
          validateOnChange
          onValuesChange={values => {
            setEmail(values.email);
            if (takenEmail && values.email !== takenEmail) setTakenEmail(null);
          }}
          onSubmit={async values => {
            setTakenEmail(null);

            const response = await signup({
              ...values,
              session: storage.local.getItem('CheckoutPage.sessionId'),
              gclid: getGclid(),
            });

            const error = response.error || response.message;

            if (isEmailTaken(response)) {
              setTakenEmail(values.email);
              return { data: null, error: '' };
            }

            return {
              data: response.data,
              error,
              onSuccess: () => {
                const { token, user } = response.data;
                setLogged({
                  user: user,
                  seller: user.currentSeller,
                  token: token,
                });

                tracking.identifyUser(user._id);
                tracking.trackEvent('Signup', {
                  currency: 'USD',
                  predicted_ltv: 600,
                  userId: user._id,
                  email: user.email,
                });
                tracking.trackEvent('store_created', {
                  store_id: user.currentSeller?._id,
                  is_first_store: true,
                  store_count: 1,
                });
                tracking.setUser({
                  $email: user.email,
                  $name: user.name,
                  $created: new Date(),
                  $seller: user.currentSeller?._id,
                });

                const onboardingPath = `/dashboard/seller/start?${stringify({
                  step: 1,
                  ...(createFromTemplateId && {
                    template: createFromTemplateId,
                  }),
                  ...(createFromPageId && {
                    page: createFromPageId,
                  }),
                })}`;
                history.push(newAccountPath(authRedirectPath(location.search), onboardingPath));
              },
            };
          }}
          showState={false}
          customFieldPropsAreEqual={invalidFieldPropsAreEqual}
          inputClassName={authInputClassName}
          renderField={renderAuthField}
          fields={[
            {
              name: 'email',
              label: 'Email',
              placeholder: 'name@example.com',
              type: 'email',
              required: true,
              autoComplete: 'username',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
              onBlur: handleEmailBlur,
              'aria-invalid': takenEmail ? true : undefined,
              additionalContent: takenEmail && (
                <AuthFieldError>
                  An account with this email already exists.{' '}
                  <AuthErrorLink path="/login" email={takenEmail}>
                    Log in instead
                  </AuthErrorLink>
                </AuthFieldError>
              ),
            },
            newPasswordField,
          ]}
          submitButton={{
            label: 'Create account',
            appearance: 'primary',
            size: 'full',
            className: authButtonClassName,
          }}
        />

        <p className="mt-4 flex gap-1.5 text-sm text-gray-700 dark:text-muted-foreground">
          <Check
            className="mt-0.5 size-4 shrink-0 text-[#346538]"
            strokeWidth={2.5}
            aria-hidden="true"
          />
          Free up to $3K in monthly sales. No credit card needed.
        </p>

        <AuthTerms />

        <AuthFooter>
          Already have an account?{' '}
          <AuthLink path="/login" email={email}>
            Log in
          </AuthLink>
        </AuthFooter>
      </AuthLayout>
    </>
  );
}
